import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import StoryCard from '../components/common/StoryCard';
import AdBanner from '../components/common/AdBanner';
import { PromoBannerGrid, ContestBanner } from '../components/common/PromoBanners';
import useSwipe from '../hooks/useSwipe';
import ContinueReading from '../components/common/ContinueReading';
import { renderAuthor } from '../utils/authorDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global countdown for Free Book of the Week.
// Driven by the book's server-provided free_end_at — identical for every user.
// Format: "Ends in: 3d 12h 45m" (no seconds). Re-ticks once per minute.
const FreeBookCountdown = ({ endAt }) => {
  const compute = () => {
    if (!endAt) return null;
    const diff = new Date(endAt).getTime() - Date.now();
    if (isNaN(diff) || diff <= 0) return null;
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return { d, h, m };
  };
  const [t, setT] = useState(compute);
  useEffect(() => {
    setT(compute());
    const id = setInterval(() => setT(compute()), 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endAt]);
  if (!t) return null;
  const label = `Ends in: ${t.d}d ${t.h}h ${t.m}m`;
  return (
    <span
      className="absolute bottom-2 right-2 bg-black/70 text-amber-300 text-[11px] sm:text-xs font-semibold px-2 py-1 rounded flex items-center gap-1 backdrop-blur-sm shadow-sm"
      data-testid="free-book-countdown"
      aria-label={label}
    >
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
      {label}
    </span>
  );
};

const apiCall = async (method, url, data = null) => {
  const token = localStorage.getItem('token');
  const config = {
    method,
    url: `${API}${url}`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
  if (data) config.data = data;
  return axios(config);
};

const HomePage = ({ Header, Footer, useAuth }) => {
  const { user } = useAuth();
  const [themes, setThemes] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [newStories, setNewStories] = useState([]);
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  // Admin-bound promo content per hero slot 1..5 (slot 0 is fixed Contest).
  // Each entry is either `null` (use fallback slide design) or a payload
  // with the same shape AdBanner consumes.
  const [heroPromos, setHeroPromos] = useState([null, null, null, null, null]);
  // Reset auto-rotation timer when the user swipes / clicks a dot so the
  // chosen slide gets a fresh 5s instead of the residual of the current
  // interval. Press-and-hold (Stories-style) also pauses the cycle.
  const [heroRotationKey, setHeroRotationKey] = useState(0);
  const heroProgressRef = useRef(null);
  const heroTimerRef = useRef(null);
  const heroCycleStartRef = useRef(0);
  const heroRemainingRef = useRef(5000);
  const heroPausedRef = useRef(false);
  const heroSlidesLenRef = useRef(0);
  const HERO_MS = 12000;

  const advanceHero = useCallback(() => {
    if (heroSlidesLenRef.current === 0) return;
    setCurrentHeroSlide((prev) => (prev + 1) % heroSlidesLenRef.current);
    setHeroRotationKey((k) => k + 1);
  }, []);
  const [expandedGenre, setExpandedGenre] = useState(null);
  const [genreStories, setGenreStories] = useState([]);
  const [loadingGenre, setLoadingGenre] = useState(false);
  const [genres, setGenres] = useState([]);
  const [personalizedFeed, setPersonalizedFeed] = useState({
    because_you_read: { story_title: '', stories: [] },
    recommended: []
  });
  const [freeBooks, setFreeBooks] = useState([]);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [themesRes, featuredRes, trendingRes, genresRes, newStoriesRes] = await Promise.all([
        axios.get(`${API}/themes`),
        axios.get(`${API}/stories/featured`),
        axios.get(`${API}/stories/trending`),
        axios.get(`${API}/genres`),
        axios.get(`${API}/stories/new?limit=10`)
      ]);
      setThemes(Array.isArray(themesRes.data) ? themesRes.data : []);
      setFeatured(Array.isArray(featuredRes.data) ? featuredRes.data : []);
      setTrending(Array.isArray(trendingRes.data) ? trendingRes.data : []);
      setGenres(Array.isArray(genresRes.data) ? genresRes.data : []);
      setNewStories(Array.isArray(newStoriesRes.data) ? newStoriesRes.data : []);
      
      // Load free books
      try {
        const freeBooksRes = await axios.get(`${API}/free-books`);
        setFreeBooks(Array.isArray(freeBooksRes.data?.stories) ? freeBooksRes.data.stories : []);
      } catch {}
      
      // Load personalized feed for logged-in users
      if (localStorage.getItem('token')) {
        try {
          const feedRes = await apiCall('get', '/feed/personalized');
          // Ensure personalized feed has proper structure
          const feed = feedRes?.data || {};
          setPersonalizedFeed({
            because_you_read: {
              story_title: feed?.because_you_read?.story_title || '',
              stories: Array.isArray(feed?.because_you_read?.stories) ? feed.because_you_read.stories : []
            },
            recommended: Array.isArray(feed?.recommended) ? feed.recommended : []
          });
        } catch (e) {
          console.log('Personalized feed not available');
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Hero slot promos — admin-targeted overrides for hero slides 1..5.
  // Empty slots fall back to the hardcoded slide design so the carousel
  // never gaps. One-shot fetch; admin changes show up on next page load.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/hero/slots`);
        const slots = r?.data?.slots || [];
        if (!alive) return;
        const next = [null, null, null, null, null];
        for (const sl of slots) {
          const m = /^hero-slot-(\d)$/.exec(sl.slot || '');
          if (!m) continue;
          const idx = parseInt(m[1], 10) - 1; // hero-slot-1 → index 0
          if (idx >= 0 && idx < 5) next[idx] = sl.item;
        }
        setHeroPromos(next);
      } catch { /* leave fallbacks */ }
    })();
    return () => { alive = false; };
  }, []);

  // Load stories when a genre is expanded
  const handleGenreClick = async (genreName) => {
    if (expandedGenre === genreName) {
      setExpandedGenre(null);
      setGenreStories([]);
      return;
    }
    
    setExpandedGenre(genreName);
    setLoadingGenre(true);
    
    try {
      // Find genre ID from genres list
      const genre = genres.find(g => g.name.toLowerCase() === genreName.toLowerCase() || 
                                      g.name.toLowerCase().includes(genreName.toLowerCase().replace('s', '')));
      if (genre) {
        const response = await axios.get(`${API}/stories?genre_id=${genre.id}&status=PUBLISHED`);
        setGenreStories(response.data.slice(0, 6)); // Show top 6 stories
      } else {
        // Fallback: search by genre name
        const response = await axios.get(`${API}/stories?search=${genreName}&status=PUBLISHED`);
        setGenreStories(response.data.slice(0, 6));
      }
    } catch (error) {
      console.error('Failed to load genre stories:', error);
      setGenreStories([]);
    }
    setLoadingGenre(false);
  };

  // Get genre ID for navigation
  const getGenreId = (genreName) => {
    const genre = genres.find(g => g.name.toLowerCase() === genreName.toLowerCase() || 
                                    g.name.toLowerCase().includes(genreName.toLowerCase().replace('s', '')));
    return genre?.id || '';
  };

  const heroSlides = [
    {
      title: "WRITING CONTEST",
      subtitle: "$1,500 IN PRIZES",
      cta: "Enter Contest",
      bg: "from-[#1a0a1f] via-[#2d1b3d] to-[#1a0a1f]",
      image: "https://static.prod-images.emergentagent.com/jobs/4e6cd94b-f232-4ec8-b6c9-8e8fedf1195d/images/e953a2f6fec8739a214b38c75653e7d657ff480e2799814b8ad05abb23def119.png",
      link: "/contest",
      isWritingContest: true
    },
    {
      title: "READERS MONTHLY CONTEST",
      subtitle: "CLIMB THE LEADERBOARD",
      cta: "Join Now",
      bg: "from-amber-900 via-yellow-900 to-orange-900",
      image: "https://static.prod-images.emergentagent.com/jobs/27b7da0a-f9ec-493e-ad62-c59ba24688c6/images/ca8ba470971c725d2f5891dc22ce80fb071234a9e74eb9162d2a2f94e80c5fa4.png",
      link: "/contest/monthly",
      isReadersContest: true
    },
    {
      title: "Shifter Romance",
      subtitle: "Unleash your wild side",
      cta: "Discover Shifters",
      bg: "from-orange-900 to-amber-950",
      image: "https://static.prod-images.emergentagent.com/jobs/27b7da0a-f9ec-493e-ad62-c59ba24688c6/images/590cb5d729c244766e49d53dfa7639eaff9427b3d8c3e1def597bf678d4cdaca.png",
      link: "/genre/shifters",
      isGenre: true
    },
    {
      title: "Dark Romance",
      subtitle: "Embrace the darkness within",
      cta: "Explore Dark Romance",
      bg: "from-red-950 to-black",
      image: "https://static.prod-images.emergentagent.com/jobs/27b7da0a-f9ec-493e-ad62-c59ba24688c6/images/8d6cb2be4aeee68b64067a3512b9cdead44ee4311ac4a2160b285c817c25d9a6.png",
      link: "/genre/dark-romance",
      isGenre: true
    },
    {
      title: "Romantasy",
      subtitle: "Where magic meets passion",
      cta: "Enter the Fantasy",
      bg: "from-purple-900 to-pink-950",
      image: "https://static.prod-images.emergentagent.com/jobs/27b7da0a-f9ec-493e-ad62-c59ba24688c6/images/3463c04c29f9f6409ae17bcda9b4618881fae329d2eae9d12275e7cc6e5b9500.png",
      link: "/genre/romantasy",
      isGenre: true
    },
    {
      title: "Contemporary",
      subtitle: "Modern love stories",
      cta: "Explore Contemporary",
      bg: "from-teal-900 to-cyan-950",
      image: "https://static.prod-images.emergentagent.com/jobs/27b7da0a-f9ec-493e-ad62-c59ba24688c6/images/a9a3c1181e89e2927cd9442f74dcabc99cc91c3fa86b1dffcda8ef356618b40f.png",
      link: "/genre/contemporary",
      isGenre: true
    }
  ];

  // Keep the live-length ref in sync so `advanceHero` (declared above)
  // can wrap without needing heroSlides as a useCallback dep.
  heroSlidesLenRef.current = heroSlides.length;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Auto-rotate hero slider. `heroRotationKey` resets the cycle on
    // user interaction (swipe / dot click). Press-and-hold pause is
    // handled by `pauseHeroRotation` / `resumeHeroRotation` using
    // setTimeout instead of setInterval so we can carry over remaining
    // time across a pause.
    if (heroSlides.length === 0) return;
    heroPausedRef.current = false;
    heroCycleStartRef.current = Date.now();
    heroRemainingRef.current = HERO_MS;
    heroTimerRef.current = setTimeout(advanceHero, HERO_MS);
    // Reset the thin progress bar in lock-step with the cycle.
    if (heroProgressRef.current) {
      const el = heroProgressRef.current;
      el.style.transition = 'none';
      el.style.width = '0%';
       
      void el.offsetWidth;
      el.style.transition = `width ${HERO_MS}ms linear`;
      el.style.width = '100%';
    }
    return () => clearTimeout(heroTimerRef.current);
  }, [heroRotationKey]);

  // Wraparound + nudge auto-rotation on every user-driven slide change.
  const goToHeroSlide = useCallback((next) => {
    const len = heroSlides.length || 1;
    setCurrentHeroSlide(((next % len) + len) % len);
    setHeroRotationKey((k) => k + 1);
  }, [heroSlides.length]);

  // ---- Press-and-hold pause/resume for the hero ------------------------
  const pauseHeroRotation = useCallback(() => {
    if (heroPausedRef.current || heroSlides.length === 0) return;
    heroPausedRef.current = true;
    clearTimeout(heroTimerRef.current);
    const elapsed = Date.now() - heroCycleStartRef.current;
    heroRemainingRef.current = Math.max(300, HERO_MS - elapsed);
    const el = heroProgressRef.current;
    if (el) {
      const px = getComputedStyle(el).width;
      el.style.transition = 'none';
      el.style.width = px;
    }
  }, [heroSlides.length]);

  const resumeHeroRotation = useCallback(() => {
    if (!heroPausedRef.current || heroSlides.length === 0) return;
    heroPausedRef.current = false;
    const dur = heroRemainingRef.current;
    heroCycleStartRef.current = Date.now() - (HERO_MS - dur);
    heroTimerRef.current = setTimeout(advanceHero, dur);
    const el = heroProgressRef.current;
    if (el) {
      const trackWidth = el.parentElement?.offsetWidth || 1;
      const currentPx = parseFloat(getComputedStyle(el).width) || 0;
      const currentPct = (currentPx / trackWidth) * 100;
      el.style.transition = 'none';
      el.style.width = `${currentPct}%`;
       
      void el.offsetWidth;
      el.style.transition = `width ${dur}ms linear`;
      el.style.width = '100%';
    }
  }, [heroSlides.length]);

  const heroSwipeHandlers = useSwipe({
    onSwipeLeft:  () => goToHeroSlide(currentHeroSlide + 1),
    onSwipeRight: () => goToHeroSlide(currentHeroSlide - 1),
    onPressStart: pauseHeroRotation,
    onPressEnd:   resumeHeroRotation,
  });

  const genreCards = [
    { name: "Romantasy", icon: "👑", color: "from-purple-500 to-pink-500" },
    { name: "Shifters", icon: "🌙", color: "from-orange-500 to-yellow-500" },
    { name: "Dark Romance", icon: "👻", color: "from-gray-700 to-gray-900" },
    { name: "Contemporary", icon: "☕", color: "from-teal-500 to-cyan-500" }
  ];

  const themeCards = [
    { name: "Enemies to Lovers", icon: "⚔️" },
    { name: "Forbidden Love", icon: "🔥" },
    { name: "Second Chance", icon: "💕" },
    { name: "Slow Burn", icon: "✨" },
    { name: "Mafia", icon: "🎭" },
    { name: "Billionaire", icon: "💎" },
    { name: "Werewolf", icon: "🐺" },
    { name: "Fantasy", icon: "🧚" }
  ];

  return (
    <div data-testid="home-page" className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1">
        {/* NEW: Hero Carousel */}
        <section
          className="relative h-[260px] sm:h-[320px] md:h-[400px] overflow-hidden touch-pan-y select-none"
          {...heroSwipeHandlers}
        >
          {heroSlides.map((slide, index) => {
            // Slots 1..5 map to heroPromos[0..4]. Index 0 is the fixed
            // Contest slide and is never promo-driven.
            const promo = index >= 1 && index <= 5 ? heroPromos[index - 1] : null;
            const onActivate = () => {
              if (promo) navigate(promo.cta_url || `/story/${promo.id}`);
              else navigate(slide.link);
            };
            return (
            <div
              key={index}
              onClick={onActivate}
              className={`absolute inset-0 transition-opacity duration-1000 cursor-pointer ${
                index === currentHeroSlide ? 'opacity-100 z-[1]' : 'opacity-0 z-0 pointer-events-none'
              }`}
              data-testid={`hero-slide-${index}`}
              data-hero-promo-id={promo ? promo.id : undefined}
            >
              {promo ? (
                /* ---- Admin-promoted cinematic hero (Feb 25, 2026) ----
                   Mirrors AdBanner's visual language so the entire promo
                   surface feels like one ecosystem. */
                <>
                  <div className="absolute inset-0 bg-[#0b0613]" />
                  {promo.cover_image && (
                    <img
                      src={promo.cover_image}
                      alt={promo.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-90"
                      loading="eager"
                    />
                  )}
                  {/* Layered cinematic gradients */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />

                  {/* Top-right meta pill */}
                  <div className="absolute top-3 right-3 sm:top-5 sm:right-5">
                    <span className="px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold tracking-wide uppercase bg-gradient-to-r from-pink-500/90 to-rose-500/90 text-white backdrop-blur-sm">
                      ★ Featured
                    </span>
                  </div>

                  <div className="relative h-full flex items-end pb-10 sm:items-center sm:pb-0">
                    <div className="container mx-auto px-4 sm:px-6">
                      <div className="max-w-xl">
                        <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-pink-300/90 mb-1.5 sm:mb-2 font-medium">
                          Storyfyc Picks
                        </p>
                        <h2
                          className="text-white font-bold leading-tight tracking-tight text-xl sm:text-4xl md:text-5xl mb-1.5 sm:mb-3 line-clamp-2"
                          style={{ fontFamily: 'Georgia, "Iowan Old Style", "Times New Roman", serif' }}
                          data-testid={`hero-promo-title-${index}`}
                        >
                          {promo.title}
                        </h2>
                        {promo.blurb && (
                          <p className="hidden sm:block text-white/75 text-sm sm:text-base md:text-lg line-clamp-2 mb-3 sm:mb-4">
                            {promo.blurb}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:gap-x-4 sm:gap-y-1.5 text-white/70 text-[11px] sm:text-sm mb-2.5 sm:mb-5">
                          {promo.views > 0 && (
                            <span className="inline-flex items-center gap-1.5">
                              <span aria-hidden>👁</span>
                              <strong className="text-white font-semibold">
                                {promo.views >= 1000 ? `${(promo.views / 1000).toFixed(1).replace(/\.0$/, '')}K` : promo.views}
                              </strong>
                              <span>reads</span>
                            </span>
                          )}
                          {promo.votes_count > 0 && (
                            <span className="inline-flex items-center gap-1.5">
                              <span aria-hidden>★</span>
                              <strong className="text-white font-semibold">
                                {promo.votes_count >= 1000 ? `${(promo.votes_count / 1000).toFixed(1).replace(/\.0$/, '')}K` : promo.votes_count}
                              </strong>
                              <span>votes</span>
                            </span>
                          )}
                          {(promo.tropes || []).slice(0, 2).map((t) => (
                            <span key={t} className="px-2 py-0.5 rounded-full bg-white/10 text-white/80 text-[11px]">
                              {t}
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); onActivate(); }}
                          className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold shadow-[0_8px_30px_-8px_rgba(255,255,255,0.6)] hover:bg-pink-100 transition-colors"
                        >
                          <span>Read Now</span>
                          <span aria-hidden>→</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : slide.isWritingContest ? (
                <>
                  {/* Writing Contest slide - 3-month contest for AUTHORS */}
                  <div className="absolute inset-0 bg-black"></div>
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
                  
                  <div className="relative h-full flex items-center pb-8 sm:pb-0">
                    <div className="container mx-auto px-4">
                      <div className="max-w-xl">
                        <div className="inline-block bg-purple-500/80 px-2 sm:px-4 py-1 rounded mb-2 sm:mb-4">
                          <span className="text-white font-bold text-xs sm:text-sm tracking-wider">MAY 1, 2026 - FEBRUARY 1, 2027</span>
                        </div>
                        <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-1 sm:mb-2 tracking-tight leading-none">
                          STORY FYC
                        </h1>
                        <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold text-purple-300 mb-2 sm:mb-4">
                          {slide.title}
                        </h2>
                        <div className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-yellow-400 mb-2 sm:mb-6 drop-shadow-lg">
                          {slide.subtitle}
                        </div>
                        <p className="hidden sm:block text-lg text-white/80 mb-6 italic">
                          "Write the love. Build the legacy."
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(slide.link); }}
                          className="bg-purple-500 hover:bg-purple-400 text-white px-5 sm:px-8 py-2.5 sm:py-4 rounded-full font-bold text-sm sm:text-lg transition-all transform hover:scale-105 shadow-lg"
                        >
                          ✍️ {slide.cta}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : slide.isReadersContest ? (
                <>
                  {/* Readers Monthly Contest slide - for all users */}
                  <div className="absolute inset-0 bg-black"></div>
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
                  
                  <div className="relative h-full flex items-center pb-8 sm:pb-0">
                    <div className="container mx-auto px-4">
                      <div className="max-w-xl">
                        <div className="inline-block bg-gradient-to-r from-amber-500 to-yellow-500 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full mb-2 sm:mb-4">
                          <span className="text-black font-bold text-xs sm:text-sm tracking-wider">🏆 STARTS MAY 2026</span>
                        </div>
                        <h1 className="text-2xl sm:text-4xl md:text-6xl font-black text-white mb-1 sm:mb-2 tracking-tight leading-none">
                          {slide.title}
                        </h1>
                        <div className="text-lg sm:text-3xl md:text-4xl font-black text-amber-400 mb-2 sm:mb-4 drop-shadow-lg">
                          {slide.subtitle}
                        </div>
                        <p className="hidden sm:block text-lg text-white/80 mb-6">
                          Share stories, earn coins, and climb the leaderboard! Open to all readers & authors.
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(slide.link); }}
                          className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black px-5 sm:px-8 py-2.5 sm:py-4 rounded-full font-bold text-sm sm:text-lg transition-all transform hover:scale-105 shadow-lg shadow-amber-500/30"
                        >
                          🚀 {slide.cta}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : slide.isGenre ? (
                <>
                  {/* Genre slides with dramatic full-bleed imagery */}
                  <div className="absolute inset-0 bg-black"></div>
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20"></div>
                  
                  <div className="relative h-full flex items-center pb-8 sm:pb-0">
                    <div className="container mx-auto px-4">
                      <div className="max-w-xl">
                        <h1 className="text-3xl sm:text-5xl md:text-7xl font-black text-white mb-2 sm:mb-4 tracking-tight leading-none drop-shadow-2xl">
                          {slide.title.toUpperCase()}
                        </h1>
                        <p className="text-base sm:text-xl md:text-2xl text-white/90 mb-3 sm:mb-8 italic font-light">
                          {slide.subtitle}
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(slide.link); }}
                          className="bg-purple-500 hover:bg-purple-400 text-white px-5 sm:px-8 py-2.5 sm:py-4 rounded-full font-bold text-sm sm:text-lg transition-all transform hover:scale-105 shadow-lg flex items-center gap-2 sm:gap-3"
                        >
                          <span>📖</span>
                          {slide.cta}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Fallback for other slides */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${slide.bg}`}></div>
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-90"
                  />
                  <div className="relative h-full flex items-center justify-center">
                    <div className="text-center max-w-3xl px-4">
                      <h1 className="text-6xl font-bold text-white mb-4">{slide.title}</h1>
                      <p className="text-2xl text-white/80 mb-8">{slide.subtitle}</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate('/discover'); }}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-8 py-4 rounded-full transition-all flex items-center gap-2 mx-auto text-lg font-semibold"
                      >
                        <span>📖</span>
                        {slide.cta}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            );
          })}
          
          {/* Navigation Arrows — hidden on mobile (swipe instead) */}
          <button
            onClick={(e) => { e.stopPropagation(); goToHeroSlide(currentHeroSlide - 1); }}
            className="hidden sm:flex absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm text-white w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full items-center justify-center hover:bg-white/30 transition-all z-10 text-lg sm:text-xl md:text-2xl"
            data-testid="hero-prev-btn"
            aria-label="Previous slide"
          >
            ‹
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goToHeroSlide(currentHeroSlide + 1); }}
            className="hidden sm:flex absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm text-white w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full items-center justify-center hover:bg-white/30 transition-all z-10 text-lg sm:text-xl md:text-2xl"
            data-testid="hero-next-btn"
            aria-label="Next slide"
          >
            ›
          </button>

          {/* Slide Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); goToHeroSlide(index); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentHeroSlide ? 'bg-white w-8' : 'bg-white/50'
                }`}
                data-testid={`hero-indicator-${index}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Thin progress bar — synced to the 5s auto-rotation. */}
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 z-10 pointer-events-none" data-testid="hero-progress-track">
            <div
              ref={heroProgressRef}
              className="h-full bg-gradient-to-r from-pink-400 to-purple-400"
              style={{ width: '0%' }}
            />
          </div>
        </section>

        <div className="container mx-auto px-4 py-4 sm:py-8">
          {/* Trending Stories - Horizontal Carousel (moved above Continue Reading) */}
          <section className="mb-8 sm:mb-12" data-testid="trending-section">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark-text">Trending Now</h2>
              <button
                onClick={() => navigate('/discover')}
                className="text-dark-accent hover:text-dark-accent-hover text-sm font-semibold"
                data-testid="trending-view-all"
              >
                View all →
              </button>
            </div>
            <div className="overflow-x-auto no-scrollbar">
              <div className="flex gap-4 sm:gap-6 pb-4">
                {(Array.isArray(trending) ? trending : []).map((story) => (
                  <StoryCard
                    key={story.id}
                    story={story}
                    hideMeta
                    onClick={() => navigate(`/story/${story.id}`)}
                  />
                ))}
                {trending.length === 0 && (
                  <p className="text-dark-text-secondary py-8">No trending stories yet</p>
                )}
              </div>
            </div>
          </section>

          {/* Fresh Reads - Horizontal Carousel (moved above Continue Reading) */}
          <section className="mb-8 sm:mb-12" data-testid="fresh-reads-section">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark-text flex items-center gap-2">
                <span className="text-green-400">✨</span> Fresh Reads
              </h2>
              <button
                onClick={() => navigate('/new-stories')}
                className="text-dark-accent hover:text-dark-accent-hover text-sm font-semibold"
                data-testid="fresh-reads-view-all"
              >
                View all →
              </button>
            </div>
            <div className="overflow-x-auto no-scrollbar">
              <div className="flex gap-4 sm:gap-6 pb-4">
                {(Array.isArray(newStories) ? newStories : []).map((story) => (
                  <div key={story.id} className="relative">
                    <span className="absolute top-2 left-2 z-10 bg-green-500 text-white px-2 py-0.5 rounded text-[10px] font-semibold">
                      NEW
                    </span>
                    <StoryCard
                      story={story}
                      hideMeta
                      onClick={() => navigate(`/story/${story.id}`)}
                    />
                  </div>
                ))}
                {newStories.length === 0 && (
                  <p className="text-dark-text-secondary py-8">No new stories yet. Be the first to publish!</p>
                )}
              </div>
            </div>
          </section>

          {/* Continue Reading Section - Only shows if user has reading progress */}
          <ContinueReading user={user} />

          {/* Free Books This Week */}
          <section className="mb-8 sm:mb-12" data-testid="free-books-section">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-dark-text flex items-center gap-2">
                  {freeBooks.length > 0 ? 'Free Books This Week' : 'No Free Books This Week'}
                </h2>
                <p className="text-dark-text-secondary text-xs sm:text-sm mt-1">
                  {freeBooks.length > 0
                    ? '3 free reads per day on selected books'
                    : 'Check back soon for new selections'}
                </p>
              </div>
            </div>
            {freeBooks.length > 0 ? (
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {freeBooks.map((story) => (
                  <div
                    key={story.id}
                    onClick={() => navigate(`/story/${story.id}`)}
                    className="flex-shrink-0 w-36 sm:w-40 md:w-48 cursor-pointer group"
                    data-testid={`free-book-${story.id}`}
                  >
                    <div className="relative overflow-hidden rounded-lg mb-2 transition-transform group-hover:scale-105">
                      <img
                        src={story.cover ? (story.cover.startsWith('http') ? story.cover : `${BACKEND_URL}${story.cover}`) : '/placeholder.jpg'}
                        alt={story.title}
                        className="w-full h-52 sm:h-60 md:h-72 object-cover bg-dark-card"
                        loading="lazy"
                        onError={(e) => { e.target.src = `${BACKEND_URL}/api/placeholder/cover`; }}
                      />
                      <span className="absolute top-2 left-2 bg-green-500/90 text-white text-[10px] font-bold px-2 py-0.5 rounded" data-testid="free-badge">
                        FREE TO READ
                      </span>
                      <FreeBookCountdown endAt={story.free_end_at || story.free_until} />
                    </div>
                    <h3 className="text-dark-text font-bold text-xs sm:text-sm mb-0.5 line-clamp-2">{story.title}</h3>
                    {renderAuthor(story) && (
                      <p className="text-dark-text-secondary text-[10px]">by {renderAuthor(story)}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="bg-dark-card border border-dark-border rounded-lg px-5 py-6 flex items-center justify-between gap-4"
                data-testid="free-books-empty-state"
              >
                <p className="text-dark-text-secondary text-sm">
                  Fresh picks drop every week. Explore the library in the meantime.
                </p>
                <button
                  onClick={() => navigate('/discover')}
                  className="shrink-0 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors"
                  data-testid="free-books-empty-cta"
                >
                  Browse Library
                </button>
              </div>
            )}
          </section>
          
          {/* Personalized "Because You Read" Section */}
          {personalizedFeed?.because_you_read?.stories?.length > 0 && (
            <section className="mb-8 sm:mb-12" data-testid="because-you-read-section">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-dark-text">
                  Because you read <span className="text-purple-400">{personalizedFeed.because_you_read.source_story}</span>
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
                {(Array.isArray(personalizedFeed?.because_you_read?.stories) ? personalizedFeed.because_you_read.stories : []).slice(0, 6).map((story, index) => (
                  <StoryCard key={story.id || index} story={story} size="small" hideMeta onClick={() => navigate(`/story/${story.id}`)} />
                ))}
              </div>
            </section>
          )}
          
          {/* Personalized Recommendations */}
          {personalizedFeed?.recommended?.length > 0 && (
            <section className="mb-8 sm:mb-12" data-testid="recommended-section">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-dark-text flex items-center gap-2">
                  <span className="text-2xl">✨</span> Recommended For You
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                {(Array.isArray(personalizedFeed?.recommended) ? personalizedFeed.recommended : []).slice(0, 5).map((story, index) => (
                  <StoryCard key={story.id || index} story={story} size="small" hideMeta onClick={() => navigate(`/story/${story.id}`)} />
                ))}
              </div>
            </section>
          )}

          {/* NEW: Genre Cards */}
          <section className="mb-8 sm:mb-12">
            <div className="flex gap-2 sm:gap-4 justify-center flex-wrap mb-4 sm:mb-8">
              {genreCards.map((genre, index) => (
                <button
                  key={index}
                  onClick={() => handleGenreClick(genre.name)}
                  className={`bg-gradient-to-r ${genre.color} text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-full font-bold text-sm sm:text-base md:text-lg flex items-center gap-2 sm:gap-3 hover:scale-105 transition-transform shadow-lg ${expandedGenre === genre.name ? 'ring-2 sm:ring-4 ring-white/50' : ''}`}
                  data-testid={`genre-btn-${genre.name.toLowerCase().replace(' ', '-')}`}
                >
                  <span className="text-lg sm:text-xl md:text-2xl">{genre.icon}</span>
                  <span className="hidden sm:inline">{genre.name}</span>
                  <span className="sm:hidden">{genre.name.split(' ')[0]}</span>
                  <span className={`transform transition-transform text-xs sm:text-base ${expandedGenre === genre.name ? 'rotate-180' : ''}`}>▼</span>
                </button>
              ))}
            </div>

            {/* Genre Stories Dropdown */}
            {expandedGenre && (
              <div className="mt-4 sm:mt-6 bg-dark-card/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-500/20 animate-fadeIn">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-dark-text flex items-center gap-2">
                    {genreCards.find(g => g.name === expandedGenre)?.icon} Top {expandedGenre} Stories
                  </h3>
                  <button
                    onClick={() => navigate(`/genre/${getGenreId(expandedGenre)}`)}
                    className="text-dark-accent hover:text-dark-accent-hover text-sm font-semibold"
                    data-testid="genre-view-all"
                  >
                    View all →
                  </button>
                </div>
                
                {loadingGenre ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  </div>
                ) : genreStories.length > 0 ? (
                  <div className="overflow-x-auto no-scrollbar">
                    <div className="flex gap-4 sm:gap-6 pb-4">
                      {(Array.isArray(genreStories) ? genreStories : []).map((story) => (
                        <StoryCard
                          key={story.id}
                          story={story}
                          hideMeta
                          onClick={() => navigate(`/story/${story.id}`)}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-dark-text-secondary text-center py-8">No stories in this genre yet. Be the first to write one!</p>
                )}
              </div>
            )}
          </section>

          {/* NEW: Theme Pills */}
          <section className="mb-8 sm:mb-12">
            <div className="flex gap-2 sm:gap-3 justify-center flex-wrap">
              {themeCards.map((theme, index) => (
                <button
                  key={index}
                  onClick={() => navigate('/discover')}
                  className="bg-purple-500/20 backdrop-blur-sm border border-purple-500/30 text-dark-text px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-full hover:bg-purple-500/30 transition-all flex items-center gap-1 sm:gap-2 text-xs sm:text-sm md:text-base"
                >
                  <span>{theme.icon}</span>
                  {theme.name}
                </button>
              ))}
            </div>
          </section>

          {/* EXISTING: Popular Categories */}
          <section className="mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl font-bold text-dark-text mb-4 sm:mb-6">Popular Categories</h2>
            <div className="flex gap-3 overflow-x-auto pb-4 no-scrollbar">
              {(Array.isArray(themes) ? themes : []).map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => navigate(`/discover?theme=${theme.id}`)}
                  className="px-6 py-3 bg-dark-card text-dark-text rounded-full hover:bg-dark-accent hover:text-white whitespace-nowrap transition-all"
                  data-testid={`theme-pill-${theme.id}`}
                >
                  {theme.name}
                </button>
              ))}
            </div>
          </section>

          {/* Ad Banner */}
          <AdBanner placement="home-top" />

          {/* EXISTING: Featured Stories - Horizontal Carousel */}
          <section className="my-8 sm:my-12">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark-text">Featured Stories</h2>
              <button
                onClick={() => navigate('/discover')}
                className="text-dark-accent hover:text-dark-accent-hover text-sm font-semibold"
              >
                View all →
              </button>
            </div>
            <div className="overflow-x-auto no-scrollbar">
              <div className="flex gap-4 sm:gap-6 pb-4">
                {(Array.isArray(featured) ? featured : []).map((story) => (
                  <StoryCard
                    key={story.id}
                    story={story}
                    hideMeta
                    onClick={() => navigate(`/story/${story.id}`)}
                  />
                ))}
                {featured.length === 0 && (
                  <p className="text-dark-text-secondary py-8">No featured stories yet</p>
                )}
              </div>
            </div>
          </section>

          {/* Promotional Banners Grid */}
          <section className="my-8 sm:my-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark-text mb-4 sm:mb-6">
              Discover More
            </h2>
            <PromoBannerGrid />
          </section>

          {/* Contest Banner */}
          <section className="my-8 sm:my-12">
            <ContestBanner />
          </section>

          {/* Ad Banner */}
          <AdBanner placement="home-bottom" />
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Discover Page

export default HomePage;
